// ─── RepoListPage (ocn-S278-P0) ──────────────────────────────────
// Minimal repo picker for the "Review & fix a repo" flow.
// Shows connected GitHub repos (from the subscriber's projects list), lets
// the user select one, and lands them in subscriber-chat with ChatMode='sync_find_fix'.
// A "Connect a new repo" fallback links to the canonical three-card entry
// screen (ocn-S818-P0 Part 2 -- never the legacy addproject/wizard pages).

import React from 'react';
import { Code2, ChevronRight, GitBranch, Plus } from 'lucide-react';
import type { ProjectSummary } from '../api';

interface RepoListPageProps {
  go: (p: string) => void;
  projects: ProjectSummary[];
}

export function RepoListPage({ go, projects }: RepoListPageProps): React.ReactElement {
  // Only show projects that have a repo slug — these are the "connected" ones.
  const connectedRepos = projects.filter((p: ProjectSummary) => p.repo && p.project_id !== 'example-app');

  const handleSelectRepo = (projectId: string) => {
    // Navigate to subscriber-chat in sync_find_fix mode, carrying the project id.
    // The pageRegistry renders SubscriberChat with the mode pre-set.
    go(`subscriber-chat:sync_find_fix:${projectId}`);
  };

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '32px 0' }}>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: '0 0 6px', fontSize: 'var(--text-xl)', fontWeight: 700, color: 'var(--text)' }}>
          Pick a repo to review
        </h2>
        <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          OCNexus will run an architecture review, surface findings, and open verified fix PRs.
        </p>
      </div>

      {connectedRepos.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <GitBranch size={32} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
          <div style={{ fontSize: 'var(--text-base)', color: 'var(--text-dim)', marginBottom: 16 }}>
            No repos connected yet.
          </div>
          <button className="btn btn-primary" onClick={() => go('new-project')}>
            <Plus size={14} /> Connect a repo
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {connectedRepos.map((p: ProjectSummary) => (
            <div
              key={p.project_id}
              className="card"
              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px' }}
              onClick={() => handleSelectRepo(p.project_id)}
            >
              <div style={{ width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#22c55e1a', color: '#22c55e', flexShrink: 0 }}>
                <Code2 size={18} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 'var(--text-base)', fontWeight: 600, color: 'var(--text)' }}>
                  {p.display_name}
                </div>
                {p.repo && (
                  <div className="mono" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)', marginTop: 2 }}>
                    {p.repo}
                  </div>
                )}
              </div>
              <ChevronRight size={16} style={{ color: 'var(--border)', flexShrink: 0 }} />
            </div>
          ))}
        </div>
      )}

      <div style={{ marginTop: 20, display: 'flex', gap: 12, alignItems: 'center' }}>
        <button className="btn" onClick={() => go('new-project')}>
          <Plus size={13} /> Connect a new repo
        </button>
        <button className="btn" style={{ color: 'var(--text-muted)' }} onClick={() => go('overview')}>
          Back
        </button>
      </div>
    </div>
  );
}

export default RepoListPage;
